import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import apiClient from '@/utils/api';

export const useVoiceRecorder = ({ studentId, onChange, onTranscript, onBusyChange, active }) => {
  const [recording, setRecording] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recorderRef = useRef(null), streamRef = useRef(null), timerRef = useRef(null);
  const mounted = useRef(true), requestRef = useRef(null), generation = useRef(0);
  const callbacks = useRef({ onChange, onTranscript, onBusyChange });
  callbacks.current = { onChange, onTranscript, onBusyChange };

  const release = useCallback(() => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);
  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    clearInterval(timerRef.current);
  }, []);
  const transcribe = async audio => {
    const run = ++generation.current;
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    setTranscribing(true); setError(''); callbacks.current.onBusyChange(true);
    try {
      const form = new FormData(); form.append('file', audio.blob, audio.filename);
      const response = await apiClient.post(`/students/${studentId}/voice-notes/transcribe`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }, signal: controller.signal, timeout: 100000,
      });
      if (!mounted.current || run !== generation.current) return;
      callbacks.current.onChange({ ...audio, transcript: response.data.transcript, duration: response.data.duration_seconds });
      callbacks.current.onTranscript(response.data.transcript);
    } catch (e) {
      if (mounted.current && run === generation.current && e.code !== 'ERR_CANCELED') setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Transcription failed. Retry, or type a note to save with this recording.');
    } finally {
      if (mounted.current && run === generation.current) { setTranscribing(false); callbacks.current.onBusyChange(false); }
    }
  };
  const start = async () => {
    if (requesting || recording || transcribing) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is not supported in this browser. You can still type your note.'); return;
    }
    setError(''); setRequesting(true); callbacks.current.onBusyChange(true);
    const run = ++generation.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mounted.current || generation.current !== run) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 });
      recorderRef.current = recorder;
      const chunks = [], started = Date.now();
      recorder.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => {
        release();
        if (!mounted.current) return;
        setRecording(false); setRequesting(false);
        const type = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type });
        if (!blob.size) { setError('No audio was recorded. Please try again.'); callbacks.current.onBusyChange(false); return; }
        const extension = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
        const audio = { id: uuidv4(), blob, filename: `voice-note.${extension}`, transcript: '', duration: Math.min((Date.now() - started) / 1000, 180) };
        callbacks.current.onChange(audio); transcribe(audio);
      };
      recorder.onerror = () => { setError('Recording was interrupted. Check your microphone and try again.'); stop(); };
      stream.getAudioTracks().forEach(track => { track.onended = stop; });
      recorder.start(1000); setSeconds(0); setRecording(true); setRequesting(false);
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - started) / 1000); setSeconds(Math.min(elapsed, 180));
        if (elapsed >= 180) stop();
      }, 250);
    } catch (e) {
      release();
      if (!mounted.current) return;
      setRequesting(false); callbacks.current.onBusyChange(false);
      setError(['NotAllowedError', 'PermissionDeniedError'].includes(e.name)
        ? 'Microphone access was blocked. Allow microphone access in your browser settings, or type your note.'
        : 'Microphone unavailable. Check that another app is not using it, then try again.');
    }
  };
  const discard = () => {
    generation.current++; requestRef.current?.abort(); setTranscribing(false); setError('');
    callbacks.current.onChange(null); callbacks.current.onBusyChange(false);
  };
  useEffect(() => {
    if (!active) {
      stop();
      if (requesting && !recording) {
        generation.current++; setRequesting(false); callbacks.current.onBusyChange(false);
      }
    }
  }, [active, requesting, recording, stop]);
  useEffect(() => {
    mounted.current = true;
    const onHidden = () => { if (document.hidden) stop(); };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      mounted.current = false; generation.current++; requestRef.current?.abort();
      if (recorderRef.current) { recorderRef.current.onstop = null; stop(); }
      release(); document.removeEventListener('visibilitychange', onHidden);
    };
  }, [release, stop]);
  return { recording, requesting, transcribing, seconds, error, start, stop, discard, transcribe };
};